import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { DataTable } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import moment from "moment";
import { HugeiconsIcon } from "@hugeicons/react";
import { StopCircleIcon, ViewIcon } from "@hugeicons-pro/core-stroke-rounded";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { GET_PAYMENT_LINK_SUBSCRIPTIONS } from "graphql/subscriptions";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import RenderSubscriptionStatus from "components/common/RenderSubscriptionStatus";
import { formatCashFull } from "utils/formatCash";
import { SubscriptionStatus } from "enums/Subscription";
import { PaymentLinkSubscription } from "hooks/useSubscriptionData";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import { intervalSuffix } from "utils/billingInterval";

export type SubscriptionFilterCriteriaProps = {
  status: SubscriptionStatus[];
  searchTerm?: string;
  paymentLinkId?: string;
};

type SubscriptionsTableProps = {
  filterCriteria: SubscriptionFilterCriteriaProps;
  refreshIndex: number;
  readOnly: boolean;
  onViewDetails: (subscriptionId: string) => void;
  onRefresh: () => void;
};

const SubscriptionsTable: React.FC<SubscriptionsTableProps> = ({
  filterCriteria,
  refreshIndex,
  readOnly,
  onViewDetails,
  onRefresh,
}) => {
  const { t } = useTranslation();

  const [paginationVariables, setPaginationVariables] = useState({});
  const [selected, setSelected] = useState<PaymentLinkSubscription | null>(
    null,
  );
  const [cancelOpen, setCancelOpen] = useState(false);

  useEffect(() => {
    setPaginationVariables({
      first: DEFAULT_PAGINATION_SIZE,
      after: undefined,
      status: filterCriteria.status,
      searchTerm: filterCriteria.searchTerm,
      paymentLinkId: filterCriteria.paymentLinkId,
    });
  }, [filterCriteria]);

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_PAYMENT_LINK_SUBSCRIPTIONS,
      initialVariables: paginationVariables,
      extractData: extractSubscriptions,
      refetchIndex: refreshIndex,
    });

  const columns: DataColumn<PaymentLinkSubscription>[] = useMemo(
    () => [
      {
        id: "subscriber",
        header: t("Subscriber"),
        maxWidth: 208,
        cell: (subscription) =>
          subscription.payerName || subscription.payerEmail,
      },
      {
        id: "plan",
        header: t("Plan"),
        maxWidth: 160,
        cell: (subscription) => subscription.paymentLink?.name || "",
      },
      {
        id: "amount",
        header: t("Amount"),
        cell: (subscription) =>
          `${formatCashFull(subscription.chargeAmount, subscription.currency)}${intervalSuffix(
            subscription.interval,
          )}`,
      },
      {
        id: "status",
        header: t("Status"),
        cell: (subscription) => (
          <RenderSubscriptionStatus
            status={subscription.status}
            awaitingPaymentMethod={subscription.awaitingPaymentMethod}
          />
        ),
      },
      {
        id: "nextBilling",
        header: t("Next Billing"),
        cell: (subscription) =>
          subscription.nextBillingDate
            ? moment(subscription.nextBillingDate).format("ll")
            : "",
      },
      {
        id: "started",
        header: t("Started"),
        cell: (subscription) =>
          subscription.createdAt
            ? moment(subscription.createdAt).format("ll")
            : "",
      },
    ],
    [t],
  );

  const rowActions = (subscription: PaymentLinkSubscription): RowAction[] => [
    {
      label: t("View Details"),
      icon: <HugeiconsIcon icon={ViewIcon} size={22} />,
      onClick: () => onViewDetails(subscription.id),
    },
    ...(readOnly || subscription.status === SubscriptionStatus.CANCELED
      ? []
      : [
          {
            label: t("Cancel Subscription"),
            icon: <HugeiconsIcon icon={StopCircleIcon} size={22} />,
            onClick: () => {
              setSelected(subscription);
              setCancelOpen(true);
            },
          },
        ]),
  ];

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      <DataTable<PaymentLinkSubscription>
        columns={columns}
        rows={dataArray}
        getRowKey={(subscription) => subscription.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(subscription) => onViewDetails(subscription.id)}
        onScroll={handleScroll}
        emptyState={t("No subscriptions found")}
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        loadingMoreText={t("Loading more subscriptions")}
        stickyHeader
      />

      <CancelSubscriptionModal
        open={cancelOpen}
        subscription={selected}
        onClose={() => setCancelOpen(false)}
        onDone={onRefresh}
      />
    </Box>
  );
};

function extractSubscriptions(
  data: Record<string, { edges: { node: PaymentLinkSubscription }[] }>,
): PaymentLinkSubscription[] {
  const firstKey = Object.keys(data)[0];
  return data[firstKey]?.edges.map((edge) => edge.node) || [];
}

export default SubscriptionsTable;
